import { useState, useContext } from "react";
import TimeSeriesAnalysisFilter from "./TimeSeriesAnalysisFilter";
import AnalysisFilterViewModel from "./ViewModels/AnalysisFilterViewModel";
import TimeSeriesAnalysisChart from "./Charts/TimeSeriesAnalysisChart";
import TimeSeriesAnalysisTable from "./Charts/TimeSeriesAnalysisTable";
import Grid from "@mui/material/Grid2";
import TimeSeriesAnalysisViewModel from "./ViewModels/TimeSeriesAnalysisViewModel";
import { APIContext } from "../Contexts/APIContext";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TimeSeriesAnalysisExportViewModel from "./ViewModels/TimeSeriesAnalysisExportViewModel";

export default function TimeSeriesAnalysisContainer() {
    const { post, get, postPDF, postCSV } = useContext(APIContext);
    const [timeSeriesAnalysisState, setTimeSeriesAnalysisState] = useState<AnalysisFilterViewModel>(new AnalysisFilterViewModel());
    const [analysisData, setAnalysisData] = useState<TimeSeriesAnalysisViewModel[]>([]);
    const [title, setTitle] = useState("");
    const [loading, setLoading] = useState(false);
    const [timeSeriesAnalysisExport, setTimeSeriesAnalysisExport] = useState<TimeSeriesAnalysisExportViewModel>(new TimeSeriesAnalysisExportViewModel());

    const updateTimeSeriesAnalysisState = async (
        properties: Partial<AnalysisFilterViewModel>,
        callback?: (newState?: AnalysisFilterViewModel) => void
    ) => {
        setTimeSeriesAnalysisState(prev => {
            const newState = { ...prev, ...properties };
            if (callback) {
                callback(newState);
            }
            return newState;
        });
    }

    const getData = () => {
        setLoading(true);

        post('TimeSlice', `GetTimeSeriesAnalysis`, timeSeriesAnalysisState, (data: any) => {
            setAnalysisData(data);
            updateTimeSeriesAnalysisState({
                showResults: true
            });
            setLoading(false);
        });
    };

    const getTitle = () => {
        get('TimeSlice', `GetChartTitle?tripRateParam=${timeSeriesAnalysisState.tripRateParamHeaderIndex}&landUse=${timeSeriesAnalysisState.landUseMain}&subUse=${timeSeriesAnalysisState.landUseSub}`, (data: any) => {
            setTitle(data);
        });
    };
    const exportToCSV = () => {
        timeSeriesAnalysisExport.filter = timeSeriesAnalysisState;
        timeSeriesAnalysisExport.tableRows = analysisData;
        timeSeriesAnalysisExport.tripRateParameterTitle = title;
        postCSV("CSVGenerator", "GetTimeSeriesAnalysisCSV", "TimeSeriesAnalysis", timeSeriesAnalysisExport)
    };
    const exportToPDF = () => {
        timeSeriesAnalysisExport.filter = timeSeriesAnalysisState;
        timeSeriesAnalysisExport.tableRows = analysisData;
        timeSeriesAnalysisExport.tripRateParameterTitle = title;
        postPDF("PDFGenerator", "GetTimeSeriesAnalysisPDF", "TimeSeriesAnalysis", true, timeSeriesAnalysisExport)
    };
    const setChartData = (chart: string) => {
        setTimeSeriesAnalysisExport({
            ...timeSeriesAnalysisExport,
            chart: chart,
        });
    };
    return (
        <>
            {!timeSeriesAnalysisState.showResults &&
                <TimeSeriesAnalysisFilter
                    state={timeSeriesAnalysisState}
                    updateState={updateTimeSeriesAnalysisState}
                    getResults={() => { getTitle(); getData(); }}
                    loading={loading}
                />
            }
            {(timeSeriesAnalysisState.showResults && analysisData.length > 0) &&
                <Grid container sx={{ height: 'calc(100% - 64px)' /* Takes title bar into account, otherwise overflows y */ }}>
                    <Grid size={8} maxHeight="800px">
                        <TimeSeriesAnalysisChart filters={timeSeriesAnalysisState} data={analysisData} title={title} setChartData={setChartData} />
                    </Grid>
                    <Grid size={4}>
                        <Grid container spacing={1} justifyContent="flex-end">
                            <Grid>
                                <Button onClick={exportToCSV}>Export To CSV</Button>
                            </Grid>
                            <Grid>
                                <Button onClick={exportToPDF}>Export To PDF</Button>
                            </Grid>
                            <Grid>
                                <Button onClick={() => updateTimeSeriesAnalysisState({ showResults: false })}>Back To Filters</Button>
                            </Grid>
                            <TimeSeriesAnalysisTable filters={timeSeriesAnalysisState} data={analysisData} title={title} />
                            <Grid container component={Paper} size={12}>
                                <Typography>
                                    The Time Series Analysis results are based on survey counts held within the TRICS database to provide evidence of how trip rates by various modes and land uses have changed over time. These results are intended to support better decision making and the tool is designed to complement the standard trip rate calculation process, providing additional context towards a vision-led approach. This feature is a significant stage in the integration of TRICS with vision-led transport planning which may be enhanced in future updates of the system. It is important to understand, depending on the land use and other selections made in the filtering process for the Time Series Analysis, that sample sizes can have considerable variation and smaller sample sizes are subject to potential skewing by individual surveys.
                                </Typography>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            }
        </>
    );
}

